import React, { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import L from "leaflet";
import { useMap } from "react-leaflet";
import mapFn from "lodash.map";
import reduceFn from "lodash.reduce";
import filterFn from "lodash.filter";
import minFn from "lodash.min";
import maxFn from "lodash.max";
import isNumberFn from "lodash.isnumber";
import simpleheat from "./vendor/simpleheat/simpleheat.js";


function isInvalid(num) {
  return !isNumberFn(num) && !num;
}
function isValid(num) {
  return !isInvalid(num);
}
function isValidLatLngArray(arr) {
  return filterFn(arr, isValid).length === arr.length;
}
function isInvalidLatLngArray(arr) {
  return !isValidLatLngArray(arr);
}
function safeRemoveLayer(leafletMap, el) {
  const { overlayPane } = leafletMap.getPanes();
  if (overlayPane && overlayPane.contains(el)) {
    overlayPane.removeChild(el);
  }
}
function shouldIgnoreLocation(loc) {
  return isInvalid(loc.lng) || isInvalid(loc.lat);
}

export default function HeatmapLayer(props) {
  const map = useMap();

  const canvasRef = useRef(null);
  const heatRef = useRef(null);
  const frameRef = useRef(null);
  const animatingRef = useRef(false);
  const propsRef = useRef(props);

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  const getMax = useCallback((p) => (p.max || 3.0), []);
  const getRadius = useCallback((p) => (p.radius || 30), []);
  const getMaxZoom = useCallback((p) => (p.maxZoom || 18), []);
  const getMinOpacity = useCallback((p) => (p.minOpacity || 0.01), []);
  const getBlur = useCallback((p) => (p.blur || 15), []);

  const updateHeatmapRadius = useCallback((radius, blur) => {
    if (heatRef.current && radius) {
      if (typeof heatRef.current.radius === "function") {
        heatRef.current.radius(radius, blur);
      }
    }
  }, []);
  const updateHeatmapGradient = useCallback((gradient) => {
    if (heatRef.current && gradient) {
      if (typeof heatRef.current.gradient === "function") {
        heatRef.current.gradient(gradient);
      }
    }
  }, []);
  const updateHeatmapMax = useCallback((maximum) => {
    if (heatRef.current && maximum) {
      if (typeof heatRef.current.max === "function") {
        heatRef.current.max(maximum);
      }
    }
  }, []);

  const animateZoom = useCallback((e) => {
    const mapLocal = map;
    const el = canvasRef.current;
    if (!el || !mapLocal) return;

    const scale = mapLocal.getZoomScale(e.zoom);
    const offset = mapLocal._getCenterOffset(e.center)
      ._multiplyBy(-scale)
      .subtract(mapLocal._getMapPanePos());

    if (L.DomUtil.setTransform) {
      L.DomUtil.setTransform(el, offset, scale);
    } else {
      el.style[L.DomUtil.TRANSFORM] =
        L.DomUtil.getTranslateString(offset) + " scale(" + scale + ")";
    }
  }, [map]);

  const redraw = useCallback(() => {
    const el = canvasRef.current;
    const heat = heatRef.current;
    const mapLocal = map;
    const currentProps = propsRef.current;
    if (!heat || !el || !mapLocal) return;

    const r = heat._r || currentProps.radius || 30;
    const size = mapLocal.getSize();

    const maxIntensity = currentProps.max === undefined ? 1 : getMax(currentProps);

    const maxZoom = currentProps.maxZoom === undefined ? mapLocal.getMaxZoom() : getMaxZoom(currentProps);
    const v = 1 / Math.pow(2, Math.max(0, Math.min(maxZoom - mapLocal.getZoom(), 12)) / 2);

    const cellSize = r / 2;

    let panePos = { x: 0, y: 0 };
    if (mapLocal._mapPane) {
      panePos = mapLocal._getMapPanePos();
    }
    const offsetX = panePos.x % cellSize;
    const offsetY = panePos.y % cellSize;

    const getLat = currentProps.latitudeExtractor;
    const getLng = currentProps.longitudeExtractor;
    const getIntensity = currentProps.intensityExtractor;

    const inBounds = (p, bounds) => bounds.contains(p);

    const filterUndefined = (row) => filterFn(row, (c) => c !== undefined);

    const roundResults = (results) =>
      reduceFn(results, (result, row) => {
        const mapped = mapFn(filterUndefined(row), (cell) => [
          Math.round(cell[0]),
          Math.round(cell[1]),
          Math.min(cell[2], maxIntensity),
          cell[3],
        ]);
        return mapped.concat(result);
      }, []);

    const getBounds = () => new L.Bounds(L.point([-r, -r]), size.add([r, r]));

    const accumulateInGrid = (points, leafletMap, bounds) =>
      reduceFn(points, (grid, point) => {
        const latLng = [getLat(point), getLng(point)];
        if (isInvalidLatLngArray(latLng)) {
          return grid;
        }

        const p = leafletMap.latLngToContainerPoint(latLng);

        if (!inBounds(p, bounds)) {
          return grid;
        }

        const x = Math.floor((p.x - offsetX) / cellSize) + 2;
        const y = Math.floor((p.y - offsetY) / cellSize) + 2;

        grid[y] = grid[y] || [];
        const cell = grid[y][x];

        const alt = getIntensity(point);
        const k = alt * v;

        if (!cell) {
          grid[y][x] = [p.x, p.y, k, 1];
        } else {
          cell[0] = (cell[0] * cell[2] + p.x * k) / (cell[2] + k); // x
          cell[1] = (cell[1] * cell[2] + p.y * k) / (cell[2] + k); // y
          cell[2] += k;
          cell[3] += 1;
        }

        return grid;
      }, []);

    const getDataForHeatmap = (points, leafletMap) => roundResults(accumulateInGrid(points, leafletMap, getBounds(leafletMap)));

    const data = getDataForHeatmap(currentProps.points || [], mapLocal);
    const dataHeatmapBoundary = (currentProps.pointsHeatmapBoundry || []).map((p) => {
      const latLng = [p && currentProps.latitudeExtractor ? currentProps.latitudeExtractor(p) : p?.lat, p && currentProps.longitudeExtractor ? currentProps.longitudeExtractor(p) : p?.lng];
      let latlngArr;
      if (Array.isArray(p) && p.length >= 2) latlngArr = p;
      else if (p && typeof p === "object") latlngArr = [p.lat, p.lng];
      else latlngArr = latLng;

      let containerPoint = { x: 0, y: 0 };
      if (mapLocal._mapPane) {
        containerPoint = mapLocal.latLngToContainerPoint(latlngArr);
      }
      return [containerPoint.x, containerPoint.y];
    });

    if (typeof heat.clear === "function") {
      heat.clear();
    }
    if (typeof heat.data === "function") {
      heat.data(data, dataHeatmapBoundary);
    }
    if (typeof heat.draw === "function") {
      heat.draw(getMinOpacity(currentProps));
    }

    frameRef.current = null;

    if (currentProps.onStatsUpdate && currentProps.points && currentProps.points.length > 0) {
      const stats = reduceFn(data, (s, point) => {
        s.max = point[3] > s.max ? point[3] : s.max;
        s.min = point[3] < s.min ? point[3] : s.min;
        return s;
      }, { min: Infinity, max: -Infinity });
      currentProps.onStatsUpdate(stats);
    }
  }, [map, getMaxZoom, getMax, getMinOpacity]);

  const reset = useCallback(() => {
    const el = canvasRef.current;
    const heat = heatRef.current;
    const mapLocal = map;
    if (!el || !heat || !mapLocal) return;

    const topLeft = mapLocal.containerPointToLayerPoint([0, 0]);
    L.DomUtil.setPosition(el, topLeft);

    const size = mapLocal.getSize();
    if (heat._width !== size.x) {
      el.width = heat._width = size.x;
    }
    if (heat._height !== size.y) {
      el.height = heat._height = size.y;
    }

    if (heat && !frameRef.current && !mapLocal._animating) {
      const raf = (L.Util && L.Util.requestAnimFrame) ? L.Util.requestAnimFrame : window.requestAnimationFrame;
      frameRef.current = raf(() => {
        redraw();
      });
    }

    redraw();
  }, [map, redraw]);

  const fitBounds = useCallback(() => {
    const currentProps = propsRef.current;
    const points = currentProps.points || [];
    if (!points || points.length === 0) return;

    const lngs = mapFn(points, currentProps.longitudeExtractor);
    const lats = mapFn(points, currentProps.latitudeExtractor);
    const ne = { lng: maxFn(lngs), lat: maxFn(lats) };
    const sw = { lng: minFn(lngs), lat: minFn(lats) };

    if (shouldIgnoreLocation(ne) || shouldIgnoreLocation(sw)) {
      return;
    }

    map.fitBounds(L.latLngBounds(L.latLng(sw), L.latLng(ne)));
  }, [map]);

  useEffect(() => {
    const canAnimate = map.options.zoomAnimation && L.Browser.any3d;
    const zoomClass = "leaflet-zoom-" + (canAnimate ? "animated" : "hide");
    const mapSize = map.getSize();
    const transformProp = L.DomUtil.testProp(["transformOrigin", "WebkitTransformOrigin", "msTransformOrigin"]);

    const el = L.DomUtil.create("canvas", zoomClass);
    canvasRef.current = el;
    el.style[transformProp] = "50% 50%";
    el.width = mapSize.x;
    el.height = mapSize.y;

    map.getPanes().overlayPane.appendChild(el);

    el.__heatmapLayer = true;

    heatRef.current = simpleheat(el);

    reset();

    if (props.fitBoundsOnLoad) {
      fitBounds();
    }

    const onViewReset = () => {
      reset();
    };
    const onMove = () => {
      reset();
    };
    const onZoomAnim = (e) => {
      animateZoom(e);
    };

    map.on("viewreset", onViewReset);
    map.on("move", onMove);

    if (map.options.zoomAnimation && L.Browser.any3d) {
      map.on("zoomanim", onZoomAnim);
    }

    return () => {
      map.off("viewreset", onViewReset);
      map.off("move", onMove);
      if (map.options.zoomAnimation && L.Browser.any3d) {
        map.off("zoomanim", onZoomAnim);
      }

      if (frameRef.current) {
        const rafCancel = (L.Util && L.Util.cancelAnimFrame) ? L.Util.cancelAnimFrame : window.cancelAnimationFrame;
        try {
          rafCancel(frameRef.current);
        } catch (e) {
        }
        frameRef.current = null;
      }

      safeRemoveLayer(map, el);

      heatRef.current = null;
      canvasRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  useEffect(() => {
    const p = props;
    if (!heatRef.current) return;

    const nextRadius = getRadius(p);
    const nextBlur = getBlur(p);
    const nextGradient = p.gradient;
    const nextMax = getMax(p);

    updateHeatmapRadius(nextRadius, nextBlur);
    updateHeatmapGradient(nextGradient);
    updateHeatmapMax(nextMax);
  }, [props.radius, props.blur, props.gradient, props.max, getRadius, getBlur, getMax, updateHeatmapRadius, updateHeatmapGradient, updateHeatmapMax]);

  useEffect(() => {
    try {
      map.invalidateSize();
    } catch (e) {
    }
    if (props.fitBoundsOnUpdate) {
      fitBounds();
    }
    reset();
  }, [props.points, props.pointsHeatmapBoundry, props.fitBoundsOnUpdate, fitBounds, map, reset]);

  return null;
}

HeatmapLayer.propTypes = {
  points: PropTypes.array.isRequired,
  pointsHeatmapBoundry: PropTypes.array.isRequired,
  longitudeExtractor: PropTypes.func.isRequired,
  latitudeExtractor: PropTypes.func.isRequired,
  intensityExtractor: PropTypes.func.isRequired,
  fitBoundsOnLoad: PropTypes.bool,
  fitBoundsOnUpdate: PropTypes.bool,
  onStatsUpdate: PropTypes.func,
  max: PropTypes.number,
  radius: PropTypes.number,
  maxZoom: PropTypes.number,
  minOpacity: PropTypes.number,
  blur: PropTypes.number,
  gradient: PropTypes.object,
};
